import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Button } from '@bn-apps/dim-ui-react';
import i18n from '@i18n/config';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 'var(--bn-space-800)',
            textAlign: 'center',
          }}
        >
          <h1>Something went wrong</h1>
          <p style={{ marginTop: 'var(--bn-space-400)' }}>
            We apologize for the inconvenience. Please try refreshing the page.
          </p>
          <Button
            onClick={() => window.location.reload()}
            variant="primary"
            size="md"
            style={{ marginTop: 'var(--bn-space-600)' }}
          >
            Refresh Page
          </Button>
          <p style={{ marginTop: 'var(--bn-space-400)' }}>
            {i18n.t('help.contactPrompt')}{' '}
            <a href={`mailto:${i18n.t('footer.supportEmail')}`}>{i18n.t('help.contactAction')}</a>
          </p>
          {this.state.error && (
            <details style={{ marginTop: 'var(--bn-space-800)', textAlign: 'left' }}>
              <summary>Error Details</summary>
              <pre
                style={{
                  marginTop: 'var(--bn-space-400)',
                  padding: 'var(--bn-space-400)',
                  background: 'light-dark(var(--bn-color-fill-subtle-on-light), var(--bn-color-fill-subtle-on-dark))',
                  borderRadius: 'var(--bn-radius-200)',
                  overflow: 'auto',
                  maxWidth: '800px',
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
